import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Bot,
  Search,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Download,
  Clock,
  Globe,
  FileText,
  Type,
  Image,
  Link2,
  Zap,
  Gauge,
  Shield,
  Trash2,
  Info,
  ExternalLink,
  Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEOAuditBot = ({ projectId, projectName, domain, tenantName, getAuthHeaders }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isNewScanOpen, setIsNewScanOpen] = useState(false);
  const [scanDomain, setScanDomain] = useState(domain || '');

  // Whitelabel tenant name - default to "My Client PM" if not provided
  const brandName = tenantName || 'My Client PM';

  useEffect(() => {
    fetchReports();
  }, [projectId]);

  useEffect(() => {
    const pendingReport = reports.find(r => r.status === 'pending' || r.status === 'running');
    if (pendingReport) {
      const interval = setInterval(fetchReports, 3000);
      return () => clearInterval(interval);
    }
  }, [reports]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/seo-reports/project/${projectId}`, getAuthHeaders());
      setReports(res.data);
      if (!selectedReport && res.data.length > 0) {
        const completed = res.data.find(r => r.status === 'completed');
        if (completed) setSelectedReport(completed);
      } else if (selectedReport) {
        const updated = res.data.find(r => r.id === selectedReport.id);
        if (updated) setSelectedReport(updated);
      }
    } catch (error) {
      console.error('Failed to fetch SEO reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartScan = async () => {
    if (!scanDomain) {
      toast.error('Please enter a domain to scan');
      return;
    }
    setScanning(true);
    try {
      await axios.post(`${API_URL}/seo-reports`, {
        project_id: projectId,
        domain: scanDomain
      }, getAuthHeaders());
      toast.success('SEO scan started! This may take a minute.');
      setIsNewScanOpen(false);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start SEO scan');
    } finally {
      setScanning(false);
    }
  };

  const handleDownloadPDF = async (reportId) => {
    try {
      const response = await axios.get(`${API_URL}/seo-reports/${reportId}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `seo_report_${brandName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API_URL}/seo-reports/${reportId}`, getAuthHeaders());
      toast.success('Report deleted');
      if (selectedReport?.id === reportId) setSelectedReport(null);
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-amber-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'On-Page SEO': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Technical SEO': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Content': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Content Quality': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
      'Page Speed': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Images': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      'Links': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    };
    return colors[category] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const MetricCard = ({ value, label, icon: Icon, color = 'text-white' }) => (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
      <div className="flex items-center gap-3">
        {Icon && <Icon size={20} className="text-slate-400" />}
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
        </div>
      </div>
    </div>
  );

  const CheckRow = ({ label, passed, value }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-700/50 last:border-0">
      <span className="text-sm text-slate-300">{label}</span>
      <div className="flex items-center gap-2">
        {value && <span className="text-sm text-slate-400">{value}</span>}
        {passed ? (
          <CheckCircle2 size={18} className="text-green-400" />
        ) : (
          <XCircle size={18} className="text-red-400" />
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const s = selectedReport?.summary || {};

  return (
    <div className="space-y-6" data-testid="seo-audit-section">
      {/* Header - Whitelabeled */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Bot size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">{brandName} SEO Analyzer</h3>
            <p className="text-sm text-slate-500">Powered by {brandName}</p>
          </div>
        </div>
        <Button onClick={() => setIsNewScanOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="run-seo-scan-btn">
          <Search size={18} className="mr-2" /> Run New Scan
        </Button>
      </div>

      {reports.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-700 bg-slate-900">
          <CardContent className="p-12 text-center">
            <Bot size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-300">No SEO Reports Yet</h3>
            <p className="text-slate-500 mt-1 mb-4">Run your first SEO audit to analyze this project's website</p>
            <Button onClick={() => setIsNewScanOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              <Search size={18} className="mr-2" /> Start First Scan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Report List - Dark Theme */}
          <div className="lg:col-span-1">
            <div className="bg-slate-900 rounded-xl border border-slate-800 sticky top-4">
              <div className="p-3 border-b border-slate-800">
                <p className="text-sm font-medium text-slate-400">Reports</p>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    onClick={() => report.status === 'completed' && setSelectedReport(report)}
                    className={`p-3 border-b border-slate-800 cursor-pointer transition-all ${
                      selectedReport?.id === report.id ? 'bg-slate-800 border-l-4 border-l-amber-500' : 'hover:bg-slate-800/50'
                    } ${report.status !== 'completed' ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-200">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-500 truncate max-w-[140px]">{report.domain}</p>
                      </div>
                      {report.status === 'completed' ? (
                        <div className={`text-xl font-bold ${getScoreColor(report.score)}`}>{report.score}</div>
                      ) : report.status === 'running' || report.status === 'pending' ? (
                        <Loader2 size={20} className="animate-spin text-amber-500" />
                      ) : (
                        <XCircle size={20} className="text-red-500" />
                      )}
                    </div>
                    {report.status === 'completed' && (
                      <div className="flex gap-1 mt-2">
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(report.id); }}>
                          <Download size={12} className="mr-1" /> PDF
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20" onClick={(e) => { e.stopPropagation(); handleDeleteReport(report.id); }}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Report Details - Dark Theme */}
          <div className="lg:col-span-3 space-y-4">
            {selectedReport && selectedReport.status === 'completed' ? (
              <>
                {/* Score Header - Dark Theme */}
                <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                  {/* Whitelabel Brand Header */}
                  <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Heart size={20} className="text-amber-500 fill-amber-500" />
                      <span className="text-slate-300 font-medium">{brandName} SEO Analyzer</span>
                      <span className="text-slate-600">|</span>
                      <span className="text-slate-500 text-sm">Powered by {brandName}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-sm" onClick={() => handleDownloadPDF(selectedReport.id)}>
                        <Download size={14} className="mr-1" /> Download PDF
                      </Button>
                    </div>
                  </div>

                  {/* Score Section */}
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-400 text-sm uppercase tracking-wide mb-2">SEO Score</p>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-7xl font-bold ${getScoreColor(s.score)}`}>{s.score}</span>
                          <span className="text-2xl text-slate-500">/100</span>
                        </div>
                        {/* Score Bar */}
                        <div className="mt-4 w-64">
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className={`h-full ${getScoreBarColor(s.score)} rounded-full transition-all`} style={{ width: `${s.score}%` }}></div>
                          </div>
                          <p className="text-slate-400 text-sm mt-2">
                            <Globe size={14} className="inline mr-1" />
                            {selectedReport.domain}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex gap-6 mb-4">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-red-400">{s.high_severity || 0}</p>
                            <p className="text-xs text-slate-500 uppercase">High</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-amber-400">{s.medium_severity || 0}</p>
                            <p className="text-xs text-slate-500 uppercase">Medium</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-slate-400">{s.low_severity || 0}</p>
                            <p className="text-xs text-slate-500 uppercase">Low</p>
                          </div>
                        </div>
                        <p className="text-slate-500 text-sm">
                          <Clock size={14} className="inline mr-1" />
                          {new Date(selectedReport.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title & Meta Description - Dark Theme */}
                <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                  <div className="px-4 py-3 bg-blue-600 flex items-center gap-2">
                    <Type size={18} className="text-white" />
                    <span className="font-medium text-white">Title & Meta Description</span>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Title</p>
                        <span className="text-xs text-slate-600">({s.title_length || 0} chars)</span>
                        {s.title_length >= 50 && s.title_length <= 60 ? (
                          <CheckCircle2 size={14} className="text-green-400" />
                        ) : (
                          <AlertTriangle size={14} className="text-amber-400" />
                        )}
                      </div>
                      <p className="text-slate-200">{s.title || 'Not found'}</p>
                    </div>
                    <div className="border-t border-slate-800 pt-4">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Meta Description</p>
                        <span className="text-xs text-slate-600">({s.meta_description_length || 0} chars)</span>
                        {s.meta_description_length >= 150 && s.meta_description_length <= 160 ? (
                          <CheckCircle2 size={14} className="text-green-400" />
                        ) : (
                          <AlertTriangle size={14} className="text-amber-400" />
                        )}
                      </div>
                      <p className="text-slate-300 text-sm">{s.meta_description || 'Not found'}</p>
                    </div>
                  </div>
                </div>

                {/* Issues Found - Dark Theme */}
                {selectedReport.issues?.length > 0 && (
                  <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 bg-red-600 flex items-center gap-2">
                      <AlertTriangle size={18} className="text-white" />
                      <span className="font-medium text-white">Issues Found ({selectedReport.issues.length})</span>
                    </div>
                    <div className="p-4 max-h-[500px] overflow-y-auto space-y-3">
                      {selectedReport.issues.map((issue, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                          <div className="flex items-start gap-3">
                            <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getCategoryBadge(issue.category)}`}>
                                  {issue.category}
                                </span>
                              </div>
                              <p className="text-slate-200 font-medium">{issue.type?.replace(/_/g, ' ')}</p>
                              <p className="text-slate-500 text-sm mt-1">
                                <span className="text-slate-400">Location:</span> {issue.location}
                              </p>
                              <p className="text-slate-500 text-sm">
                                <span className="text-slate-400">Current:</span> {issue.current_value}
                              </p>
                              <div className="flex items-start gap-2 mt-2 p-2 bg-blue-900/20 rounded border border-blue-500/20">
                                <Info size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                                <p className="text-blue-300 text-sm">{issue.recommendation}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technical SEO - Dark Theme */}
                <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                  <div className="px-4 py-3 bg-purple-600 flex items-center gap-2">
                    <Shield size={18} className="text-white" />
                    <span className="font-medium text-white">Technical SEO</span>
                  </div>
                  <div className="p-4">
                    <CheckRow label="HTTPS" passed={s.has_https} value={s.has_https ? 'Yes' : 'No'} />
                    <CheckRow label="Canonical Tag" passed={s.has_canonical} value={s.has_canonical ? 'Yes' : 'No'} />
                    <CheckRow label="Mobile Viewport" passed={s.has_viewport} value={s.has_viewport ? 'Yes' : 'No'} />
                    <CheckRow label="Open Graph Tags" passed={s.has_open_graph} value={s.has_open_graph ? 'Yes' : 'No'} />
                    <CheckRow label="Schema Markup (JSON-LD)" passed={s.has_json_ld} value={s.has_json_ld ? 'Yes' : 'No'} />
                    {s.schema_types?.length > 0 && (
                      <div className="mt-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <p className="text-xs text-slate-400">Schema types found: <span className="text-slate-300">{s.schema_types.join(', ')}</span></p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Page Speed - Dark Theme */}
                <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                  <div className="px-4 py-3 bg-orange-500 flex items-center gap-2">
                    <Zap size={18} className="text-white" />
                    <span className="font-medium text-white">Page Speed</span>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <MetricCard value={`${s.load_time_ms || 0}ms`} label="Load Time" icon={Clock} />
                      <MetricCard value={`${s.page_size_kb || 0}KB`} label="Page Size" icon={FileText} />
                      <MetricCard value={s.script_count || 0} label="Scripts" />
                      <MetricCard value={s.stylesheet_count || 0} label="Stylesheets" />
                    </div>
                    <CheckRow label="Compression (gzip)" passed={s.has_compression} value={s.has_compression ? 'Yes' : 'No'} />
                  </div>
                </div>

                {/* Content Analysis & Quality - Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 bg-green-600 flex items-center gap-2">
                      <FileText size={18} className="text-white" />
                      <span className="font-medium text-white">Content Analysis</span>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-3">
                      <MetricCard value={s.word_count || 0} label="Words" />
                      <MetricCard value={s.h1_count || 0} label="H1 Tags" />
                      <MetricCard value={s.h2_count || 0} label="H2 Tags" />
                      <MetricCard value={s.paragraph_count || 0} label="Paragraphs" />
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 bg-teal-600 flex items-center gap-2">
                      <Gauge size={18} className="text-white" />
                      <span className="font-medium text-white">Content Quality</span>
                    </div>
                    <div className="p-4">
                      <div className="text-center mb-4">
                        <p className={`text-5xl font-bold ${s.readability_score >= 60 ? 'text-green-400' : s.readability_score >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                          {s.readability_score || 0}
                        </p>
                        <p className="text-slate-400 text-sm">Readability ({s.reading_grade || 'N/A'})</p>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-2 border-b border-slate-700/50">
                          <span className="text-slate-400">Avg Sentence Length</span>
                          <span className="text-slate-200">{s.avg_sentence_length || 0} words</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-400">Unique Words</span>
                          <span className="text-slate-200">{s.unique_words_percent || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images & Links - Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 bg-pink-600 flex items-center gap-2">
                      <Image size={18} className="text-white" />
                      <span className="font-medium text-white">Images Analysis</span>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-3">
                      <MetricCard value={s.total_images || 0} label="Total Images" />
                      <MetricCard 
                        value={s.images_missing_alt || 0} 
                        label="Missing Alt" 
                        color={s.images_missing_alt > 0 ? 'text-red-400' : 'text-green-400'} 
                      />
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 bg-indigo-600 flex items-center gap-2">
                      <Link2 size={18} className="text-white" />
                      <span className="font-medium text-white">Links Analysis</span>
                    </div>
                    <div className="p-4 grid grid-cols-3 gap-3">
                      <MetricCard value={s.total_links || 0} label="Total" />
                      <MetricCard value={s.internal_links || 0} label="Internal" color="text-blue-400" />
                      <MetricCard value={s.external_links || 0} label="External" color="text-purple-400" />
                    </div>
                  </div>
                </div>

                {/* Footer Branding */}
                <div className="text-center py-4 text-slate-500 text-sm">
                  <Heart size={14} className="inline mr-1 text-amber-500 fill-amber-500" />
                  Report generated by {brandName} SEO Analyzer
                </div>
              </>
            ) : (
              <div className="bg-slate-900 rounded-xl border border-slate-800">
                <div className="p-12 text-center">
                  <Gauge size={48} className="mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">Select a completed report to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Scan Dialog */}
      <Dialog open={isNewScanOpen} onOpenChange={setIsNewScanOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Bot size={20} className="text-amber-500" /> Run SEO Audit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Our SEO analyzer will scan your website and check for:
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {['Title & Meta tags', 'H1/H2 structure', 'Content quality', 'Page speed', 'Mobile viewport', 'Schema markup', 'Image alt text', 'Internal links'].map(item => (
                <div key={item} className="flex items-center gap-2 text-slate-300">
                  <CheckCircle2 size={14} className="text-green-400" /> {item}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Website URL *</Label>
              <div className="relative">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input 
                  value={scanDomain} 
                  onChange={(e) => setScanDomain(e.target.value)} 
                  placeholder="https://example.com" 
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" 
                  data-testid="scan-domain-input" 
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsNewScanOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleStartScan} disabled={scanning || !scanDomain} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="start-scan-btn">
                {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</> : <><Search className="w-4 h-4 mr-2" /> Start Scan</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SEOAuditBot;
